import { createContext, useState, useMemo, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import Tooltip from '../common/Tooltip'
import CardHeaderSlotContext from '../../StateManagment/CardHeaderSlot'
import CopyCardButton from './CopyCardButton'

// Lets DashboardCardOptions portal its icons into the card's bottom bar,
// so they sit outside the scrollable content and stay visible while
// the content scrolls.
export const DashboardCardFooterContext = createContext(null)

// When isOverlay is true this card is rendered inside dnd-kit's <DragOverlay>.
// The overlay is positioned outside the flex grid, so we skip useSortable
// wiring and let CSS (.new-dashboard-card--overlay) reset the grid-derived
// width so the card shows at its natural size instead of shrinking or
// stretching to match whatever slot the pointer is over.
const DashboardCard = ({ id, onDelete, extraClass, children, isOverlay = false }) => {
    const [footerEl, setFooterEl] = useState(null)
    // De kaartinhoud, voor 'Kopiëren als afbeelding'.
    const contentRef = useRef(null)
    const [counterSlot, setCounterSlot] = useState(null)
    const [infoSlot, setInfoSlot] = useState(null)
    const [filterSlot, setFilterSlot] = useState(null)
    const [expandSlot, setExpandSlot] = useState(null)
    const headerSlots = useMemo(
        () => ({ counterSlot, infoSlot, filterSlot, expandSlot }),
        [counterSlot, infoSlot, filterSlot, expandSlot]
    )

    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled: isOverlay })

    const style = isOverlay
        ? undefined
        : {
              transform: CSS.Transform.toString(transform),
              transition,
          }

    const className = [
        'new-dashboard-card',
        !isOverlay && isDragging && 'new-dashboard-card--dragging',
        isOverlay && 'new-dashboard-card--overlay',
        extraClass,
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div
            ref={isOverlay ? undefined : setNodeRef}
            style={style}
            className={className}
            {...(isOverlay ? {} : attributes)}
        >
            <CardHeaderSlotContext.Provider value={headerSlots}>
                <DashboardCardFooterContext.Provider value={footerEl}>
                    <div className='new-dashboard-card-content' ref={contentRef}>
                        {children}
                    </div>
                </DashboardCardFooterContext.Provider>
            </CardHeaderSlotContext.Provider>
            {/* Bottom bar — the card's only chrome. Order: drag handle, info
                slot, option icons (portaled by DashboardCardOptions), then
                counter + delete pinned to the right by the stretching
                options slot. */}
            <div className='new-dashboard-card-footer'>
                <span
                    className='new-dashboard-drag-handle'
                    ref={isOverlay ? undefined : setActivatorNodeRef}
                    {...(isOverlay ? {} : listeners)}
                >
                    <Tooltip content='Verslepen' top='-35px'>
                        <DragIndicatorOutlinedIcon />
                    </Tooltip>
                </span>
                <span className='new-dashboard-card-footer-divider' />
                <span className='new-dashboard-card-footer-info-slot' ref={setInfoSlot} />
                {/* Hidden via CSS when the info slot is empty */}
                <span className='new-dashboard-card-footer-divider' />
                <span className='new-dashboard-card-footer-filter-slot' ref={setFilterSlot} />
                {/* Hidden via CSS when the filter slot is empty */}
                <span className='new-dashboard-card-footer-divider' />
                {/* Niet op de sleepkopie: die is een afbeelding van de kaart, geen kaart. */}
                {!isOverlay && <CopyCardButton targetRef={contentRef} />}
                <span className='new-dashboard-card-footer-options' ref={setFooterEl} />
                <span className='new-dashboard-card-footer-counter-slot' ref={setCounterSlot} />
                {/* 'Op groot scherm' sits apart: right of the other icons,
                    left of delete, behind its own divider (hidden via CSS
                    when the slot is empty). */}
                <span className='new-dashboard-card-footer-divider new-dashboard-card-footer-expand-divider' />
                <span className='new-dashboard-card-footer-expand-slot' ref={setExpandSlot} />
                {onDelete && (
                    <>
                        <span className='new-dashboard-card-footer-divider' />
                        <Tooltip content='Verwijderen' top='-35px'>
                            <DeleteOutlineOutlinedIcon
                                className='new-dashboard-delete-icon'
                                onClick={onDelete}
                            />
                        </Tooltip>
                    </>
                )}
            </div>
        </div>
    )
}

export default DashboardCard
